// EditableBetSlipItem.js
import { useState, useRef, useEffect } from 'react';
import { X, Edit, Save, User, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import BookmakerLinks from './BookmakerLinks';

const EditableBetSlipItem = ({ bet, onRemove, onUpdate, allBets, index = 0 }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(bet.text);
  const textareaRef = useRef(null);

  // Auto-focus and resize textarea when editing starts
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      adjustTextareaHeight();
    }
  }, [isEditing]);

  // Adjust textarea height based on content
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const handleTextChange = (e) => {
    setEditedText(e.target.value);
    adjustTextareaHeight();
  };

  const handleSave = () => {
    // Prevent saving empty text
    if (editedText.trim()) {
      onUpdate(bet.id, editedText);
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e) => {
    // Save on Ctrl+Enter or Cmd+Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      handleSave();
    }
    // Cancel on Escape
    if (e.key === 'Escape') {
      setEditedText(bet.text);
      setIsEditing(false);
    }
  };

  // Detect bet type
  const isPlayerProp = bet?.parsedData?.bet_type === 'player_prop';
  const game = bet?.parsedData?.game;
  const homeTeam = game?.home_team || bet?.parsedData?.home_team;
  const awayTeam = game?.away_team || bet?.parsedData?.away_team;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`relative border rounded-lg p-3 mb-2 ${
        isPlayerProp
          ? 'bg-gradient-to-br from-purple-900/10 to-transparent border-purple-700/30'
          : 'bg-gradient-to-br from-cyan-900/10 to-transparent border-cyan-700/30'
      } bg-slate-800`}
    >
      {isEditing ? (
        // Editing mode
        <div className="flex flex-col">
          <textarea
            ref={textareaRef}
            value={editedText}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            className="w-full bg-slate-700 border border-slate-600 rounded p-2 text-sm text-slate-200 resize-none min-h-[60px] focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => {
                setEditedText(bet.text);
                setIsEditing(false);
              }}
              className="px-2 py-1 text-xs text-slate-400 hover:text-slate-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-500 text-white rounded transition-colors"
            >
              <Save className="w-3 h-3" />
              <span>Save</span>
            </button>
          </div>
        </div>
      ) : (
        // Display mode with modern card design
        <>
          {/* Card Header */}
          <div className="flex justify-between items-start mb-2">
            <div className="flex-1">
              {/* Game Info */}
              {(homeTeam || awayTeam) && (
                <div className="text-xs text-slate-400 mb-1 flex items-center gap-1">
                  {isPlayerProp ? (
                    <>
                      <User className="w-3 h-3 text-purple-400" />
                      <span className="text-purple-300">Player Prop</span>
                    </>
                  ) : (
                    <>
                      <TrendingUp className="w-3 h-3 text-cyan-400" />
                      <span className="text-cyan-300">Game Odds</span>
                    </>
                  )}
                  {awayTeam && homeTeam && (
                    <span className="ml-1">• {awayTeam} @ {homeTeam}</span>
                  )}
                </div>
              )}

              {/* Bet Description */}
              <div className="text-sm font-medium text-white">
                {isPlayerProp && bet.parsedData?.player_name && (
                  <span className="text-purple-300">{bet.parsedData.player_name} </span>
                )}
                {bet.parsedData?.selection ? (
                  <>
                    <span className="text-white">{bet.parsedData.selection.toUpperCase()}</span>
                    {bet.parsedData?.points && (
                      <span className="text-emerald-400 ml-1 font-bold">{bet.parsedData.points}</span>
                    )}
                    {isPlayerProp && bet.parsedData?.player_stat_type && (
                      <span className="text-slate-400 ml-1">
                        {bet.parsedData.player_stat_type}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-slate-200">{bet.text}</span>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-1 flex-shrink-0 ml-2">
              <button
                onClick={() => setIsEditing(true)}
                className="p-1 text-slate-400 hover:text-emerald-400 transition-colors rounded"
                title="Edit pick"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => onRemove(bet.id)}
                className="p-1 text-slate-400 hover:text-red-400 transition-colors rounded"
                title="Remove pick"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* BookmakerLinks - Always visible with improved styling */}
          <BookmakerLinks
            bet={bet}
            allBets={allBets}
            isParlay={allBets && allBets.length > 1}
            compact={true}
          />

          {/* Timestamp */}
          {bet.timestamp && (
            <div className="text-xs text-slate-500 mt-2">
              {bet.timestamp}
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

export default EditableBetSlipItem;
